import { Column, EmptyDiv, FormContext, InfoDiv } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetUser } from '@/hooks/useGetUser';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useGetPricingConfigurationQuery,
  useUpsertPlanPricingConfigurationMutation,
} from '@/store/slices/pricingConfigurationSlice';
import { Text, Button, Icon, IconType } from '@/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { PricingConfigurationValues } from '@/services/Pricing/pricing-configuration.service';

const mergeEffectiveValues = (
  systemDefaults: PricingConfigurationValues,
  orgOverrides: PricingConfigurationValues | undefined,
): PricingConfigurationValues => ({ ...systemDefaults, ...(orgOverrides ?? {}) });

function PlanPricing() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const { currentUser } = useGetUser();
  const [isReadOnly, setIsReadOnly] = useState(true);
  const organizationId = currentUser.companyId;
  const orgArg = organizationId ? { organizationId } : skipToken;

  const {
    data: defaultValues,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
  } = useGetDefaultPricingConfigurationQuery(orgArg);

  const { data: minimums, isLoading: isMinimumsLoading } =
    useGetMinimumPricingConfigurationQuery(orgArg);

  const { data: orgPricing, isLoading: isOrgPricingLoading } =
    useGetPricingConfigurationQuery(orgArg);
  const { data: existing, isLoading: isExistingLoading } = useGetPricingConfigurationQuery(
    organizationId && planId ? { organizationId, planId } : skipToken,
  );
  const [upsertPlanPricingConfig, { isLoading: isSaving }] =
    useUpsertPlanPricingConfigurationMutation();

  const effectiveDefaults: PricingConfigurationValues = useMemo(
    () => mergeEffectiveValues(defaultValues?.values ?? {}, orgPricing?.values),
    [defaultValues, orgPricing],
  );

  const { activeCountry, setActiveCountry, submitHandler } = usePricingForm({
    baseline: effectiveDefaults,
    existing: existing?.values,
    submit: (nextValues) =>
      upsertPlanPricingConfig({
        organizationId,
        planId: planId ?? '',
        payload: { values: nextValues },
      }),
    messages: {
      success: t('Messages.plan_pricing_update_success'),
      error: t('Messages.plan_pricing_update_error'),
    },
  });

  if (isDefaultsLoading || isMinimumsLoading || isOrgPricingLoading || isExistingLoading) {
    return <SkeletonList />;
  }

  if (isDefaultsError || !defaultValues) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={{ rows: [] }}>
      <Column gapSize='24'>
        {isReadOnly && (
          <InfoDiv>
            <Text size='s' weight='light' color='base-grey'>
              {t('Pricing.organization_pricing_comment')}
            </Text>
            <Button type='button' variant='outline' onClick={() => setIsReadOnly(false)}>
              <Icon type={IconType.Pencil_Edit} width={24} height={24} />
              <Text size='s' weight='medium' color='base-grey'>
                {t('Pricing.customize')}
              </Text>
            </Button>
          </InfoDiv>
        )}
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='plan'
            defaults={effectiveDefaults}
            minimums={minimums?.values}
            orgPricing={effectiveDefaults}
            existingValues={existing?.values}
            isReadOnly={isReadOnly}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
          />
        </Column>
        <PricingDiffFooter
          baseline={effectiveDefaults}
          existing={existing?.values}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default PlanPricing;
